// app/frontend/hooks/useNotifications.ts
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getCable } from '../src/cable';
import { csrfHeaders } from '../src/utils/csrf';

export interface Notification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  action_url: string | null;
  icon: string | null;
  priority: string;
  read: boolean;
  read_at: string | null;
  created_at: string;
  formatted_time: string;
  notifiable?: {
    id: number;
    name: string;
  };
  actor?: {
    id: number;
    username: string;
  };
}

export interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
  pagination?: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

export const useNotifications = (pollingInterval: number = 30000) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch recent notifications (both read and unread, limited to recent)
  const fetchUnreadNotifications = useCallback(async () => {
    try {
      // Fetch recent 20 notifications (read and unread)
      const response = await axios.get<NotificationsResponse>('/notifications.json?per_page=20');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      setError(null);
    } catch (err: any) {
      // Silently fail if user is not authenticated (401/404)
      if (err?.response?.status === 401 || err?.response?.status === 404) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        console.error('Error fetching notifications:', err);
        setError('Failed to fetch notifications');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count only (lightweight for polling)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get<{ unread_count: number }>('/notifications/count.json');
      setUnreadCount(response.data.unread_count);
    } catch (err: any) {
      // Silently fail if user is not authenticated
      if (err?.response?.status === 401 || err?.response?.status === 404) {
        setUnreadCount(0);
      } else {
        console.error('Error fetching notification count:', err);
      }
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      await axios.post(`/notifications/${notificationId}/mark_as_read.json`, null, { headers: csrfHeaders() });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.post('/notifications/mark_all_as_read.json', null, { headers: csrfHeaders() });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, []);

  // Delete a notification
  const deleteNotification = useCallback(
    async (notificationId: number) => {
      try {
        await axios.delete(`/notifications/${notificationId}.json`, { headers: csrfHeaders() });
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        // Recalculate unread count
        setUnreadCount((prev) => {
          const deletedNotification = notifications.find((n) => n.id === notificationId);
          return deletedNotification && !deletedNotification.read ? Math.max(0, prev - 1) : prev;
        });
      } catch (err) {
        console.error('Error deleting notification:', err);
      }
    },
    [notifications],
  );

  // Clear all read notifications
  const clearAllRead = useCallback(async () => {
    try {
      await axios.post('/notifications/clear_all.json', null, { headers: csrfHeaders() });
      setNotifications((prev) => prev.filter((n) => !n.read));
    } catch (err) {
      console.error('Error clearing notifications:', err);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (pollingInterval > 0) {
      fetchUnreadNotifications();
    }
  }, [fetchUnreadNotifications, pollingInterval]);

  // Set up polling for unread count (kept as a fallback in case the cable
  // subscription drops or the server can't broadcast for any reason).
  useEffect(() => {
    if (pollingInterval <= 0) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [pollingInterval, fetchUnreadCount]);

  // Live subscription to NotificationChannel. Any new notification (or read-
  // state change from another tab) updates state immediately without a fetch.
  useEffect(() => {
    if (pollingInterval <= 0) return; // opt-out signal from the consumer

    const cable = getCable();
    const subscription = cable.subscriptions.create(
      { channel: 'NotificationChannel' },
      {
        received(data: {
          event: 'created' | 'read_state_changed' | 'all_read';
          notification?: Notification;
          id?: number;
          read?: boolean;
          unread_count: number;
        }) {
          if (data.event === 'created' && data.notification) {
            setNotifications((prev) => {
              // Guard against a race where the fetch already picked this one up
              if (prev.some((n) => n.id === data.notification!.id)) return prev;
              return [data.notification!, ...prev].slice(0, 20);
            });
            setUnreadCount(data.unread_count);
          } else if (data.event === 'read_state_changed' && typeof data.id === 'number') {
            setNotifications((prev) =>
              prev.map((n) =>
                n.id === data.id ? { ...n, read: !!data.read, read_at: data.read ? new Date().toISOString() : null } : n,
              ),
            );
            setUnreadCount(data.unread_count);
          } else if (data.event === 'all_read') {
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true, read_at: new Date().toISOString() })));
            setUnreadCount(0);
          }
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [pollingInterval]);

  // When the unread count changes, refetch the list so the dropdown shows new items without a full page reload
  useEffect(() => {
    // Avoid double fetching on very first mount while loading is true
    if (!loading) {
      fetchUnreadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchUnreadNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllRead,
  };
};
